import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ShowForPermissions from 'features/security/ShowForPermissions';
import EnableForProjectState from 'features/security/EnableForProjectState';
import { Button, Tooltip } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import Avatar from 'common/components/Avatar';
import useLock from 'common/hooks/useLock';
import featureFlags from 'common/helpers/featureFlags';
import useSmallMobile from 'common/hooks/useSmallMobile';
import MoreMenu from '../ContentActions/MoreMenu';
import styles from './ActionsWithLock.module.scss';

interface IProps {
  contentThreadId: number;
  buttonOnEdit: () => void;
  hideEdit?: boolean;
  isEditDisabled: boolean;
  itemId: string;
  onDelete: () => void;
  onRefresh: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  viewClient: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflowActions: any;
  reportPreviewUrl: string;
  isTopicHeader: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  disableMoreMenu?: boolean;
}

const WrapperComponent = ({
  buttonOnEdit,
  contentThreadId,
  disableMoreMenu,
  hideEdit = false,
  isEditDisabled,
  isTopicHeader,
  itemId,
  onDelete,
  onRefresh,
  reportPreviewUrl,
  viewClient,
  workflowActions,
}: IProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { name, message, isLocked } = useLock({ contentThreadId });
  const isSmallMobile = useSmallMobile();
  const { t } = useTranslation();

  const getComponentEdit = ({
    isProjectDisabled,
  }: {
    readonly isProjectDisabled: boolean;
  }) => {
    if (hideEdit) {
      return null;
    }

    return isLocked ? (
      <Tooltip
        tip={isLocked ? message : ''}
        direction="bottom-end"
        dataTestId={undefined}
        unthemed={undefined}
        showingInitial={undefined}
      >
        <Button
          data-testid="edit-button"
          type="button"
          onClick={buttonOnEdit}
          className="button-block button-small"
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          disabled={isProjectDisabled || isEditDisabled || isLocked}
          icon={undefined}
          iconClasses={undefined}
        >
          {t('analysisMenuEdit')}
        </Button>
      </Tooltip>
    ) : (
      <Button
        data-testid="edit-button"
        type="button"
        onClick={buttonOnEdit}
        className="button-block button-small"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        disabled={isProjectDisabled || isEditDisabled || isLocked}
        icon={undefined}
        iconClasses={undefined}
      >
        {t('editKeyFocusAreaBtn')}
      </Button>
    );
  };

  return (
    <ShowForPermissions
      projectId={projectId}
      permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
    >
      <div className={styles['actions-with-lock']}>
        {isLocked && !isSmallMobile && <Avatar name={name} />}
        <EnableForProjectState projectId={projectId}>
          {/* eslint-disable-next-line react/no-unused-prop-types */}
          {({ isProjectDisabled }: { isProjectDisabled: boolean }) => (
            <>
              {getComponentEdit({ isProjectDisabled })}

              <MoreMenu
                isDisabled={disableMoreMenu || isLocked}
                itemId={itemId}
                onDelete={onDelete}
                onRefresh={onRefresh}
                onEdit={isSmallMobile ? buttonOnEdit : ''}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                viewClient={viewClient}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                workflowActions={workflowActions}
                reportPreviewUrl={reportPreviewUrl}
                isTopicHeader={isTopicHeader}
                isEditDisabled={isEditDisabled}
                isMobile
              />
            </>
          )}
        </EnableForProjectState>
      </div>
    </ShowForPermissions>
  );
};

const ActionsWithLock: React.FC<IProps> = ({
  buttonOnEdit,
  contentThreadId,
  disableMoreMenu = false,
  hideEdit,
  isEditDisabled,
  isTopicHeader,
  itemId,
  onDelete,
  onRefresh,
  reportPreviewUrl,
  viewClient,
  workflowActions,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();
  const isSmallMobile = useSmallMobile();
  if (featureFlags.isAutoSaveEnabled) {
    return (
      <WrapperComponent
        buttonOnEdit={buttonOnEdit}
        contentThreadId={contentThreadId}
        disableMoreMenu={disableMoreMenu}
        isEditDisabled={isEditDisabled}
        isTopicHeader={isTopicHeader}
        itemId={itemId}
        onDelete={onDelete}
        onRefresh={onRefresh}
        reportPreviewUrl={reportPreviewUrl}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        viewClient={viewClient}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workflowActions={workflowActions}
        hideEdit={hideEdit}
      />
    );
  }
  return (
    <ShowForPermissions
      projectId={projectId}
      permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
    >
      <EnableForProjectState projectId={projectId}>
        {/* eslint-disable-next-line react/no-unused-prop-types */}
        {({ isProjectDisabled }: { isProjectDisabled: boolean }) => (
          <>
            <Button
              data-testid="edit-button"
              type="button"
              onClick={buttonOnEdit}
              className="button-block button-small mr-3"
              disabled={isProjectDisabled || isEditDisabled}
              icon={undefined}
              iconClasses={undefined}
            >
              {t('analysisMenuEdit')}
            </Button>

            <MoreMenu
              itemId={itemId}
              onDelete={onDelete}
              onRefresh={onRefresh}
              onEdit={isSmallMobile ? buttonOnEdit : ''}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              viewClient={viewClient}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              workflowActions={workflowActions}
              reportPreviewUrl={reportPreviewUrl}
              isTopicHeader={isTopicHeader}
              isEditDisabled={isEditDisabled}
              isMobile
            />
          </>
        )}
      </EnableForProjectState>
    </ShowForPermissions>
  );
};

export default ActionsWithLock;
